import { Badge, BlockStack, Box, Button, DataTable, Divider, Grid, Icon, InlineStack, Link, SkeletonBodyText, Text } from '@shopify/polaris';
import __helpers from 'helpers/index';
import { ENUM_TRIP_MANIFEST_STATUS, tripStatusLabels, useGetTrip_manifests_by_driver } from 'queries/trip_manifest.query';
import { useCallback, useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, StatusActiveIcon } from '@shopify/polaris-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinusSquare, faQrcode, faSackDollar, faTicket } from '@fortawesome/free-solid-svg-icons';
import 'media/css/trip_manifest_report.scss';
import DateTimeInput from 'components/dateTimeInput';
import Capacity from 'components/Capacity';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useMutateCreateDriverSettlement } from 'queries/driver_settlement.query';
import { useNotification } from 'context/NotificationContext';
import { ENUM_TRANSACTION_PAYMENT_METHOD } from 'queries/transactions.query';
import { ENUM_ORDER_PAYMENT_STATUS } from 'queries/orders.query';
dayjs.extend(utc);
dayjs.extend(timezone);

export default function TripManifestReport() {
  const [currentDate, setCurrentDate] = useState(dayjs().tz('Asia/Ho_Chi_Minh').format('YYYY-MM-DD'));
  const { data: driverVisualizationData, isLoading: loadingDriverVisualization } = useGetTrip_manifests_by_driver(currentDate);

  const [expandedManifestId, setExpandedManifestId] = useState<string | null>(null);

  const getTripStatusTone = useCallback((status?: number) => {
    switch (status) {
      case ENUM_TRIP_MANIFEST_STATUS.DISPATCHED:
        return 'info' as const;
      case ENUM_TRIP_MANIFEST_STATUS.IN_TRANSIT:
        return 'success' as const;
      case ENUM_TRIP_MANIFEST_STATUS.COMPLETED:
        return 'success' as const;

      case ENUM_TRIP_MANIFEST_STATUS.FINALIZED:
        return 'success-strong' as const;
      case ENUM_TRIP_MANIFEST_STATUS.CANCELLED:
        return 'critical' as const;
      case ENUM_TRIP_MANIFEST_STATUS.DRAFT:
      default:
        return 'attention' as const;
    }
  }, []);

  /** Tính tổng trong bảng  */
  let [totalInTable, setTotalInTable] = useState({
    total_manifest: 0,
    total_fee: 0,
    total_fare: 0,
    total_due: 0,
  });

  useEffect(() => {
    if (!driverVisualizationData) return;

    const total = driverVisualizationData?.data.reduce(
      (acc, group) => {
        for (const manifest of group.trip_manifests ?? []) {
          acc.total_manifest += 1;
          acc.total_fee += manifest.total_fee;
          acc.total_fare += manifest.total_fare;
          acc.total_due += manifest.total_amount_due - manifest.total_fee;
        }
        return acc;
      },
      {
        total_manifest: 0,
        total_fee: 0,
        total_fare: 0,
        total_due: 0,
      }
    );

    setTotalInTable(total);
  }, [driverVisualizationData]);

  //   const [showDriver, setShowDriver] = useState(false);

  /** đánh dấu tạo quyết toán ... */
  const { addNotification } = useNotification();
  const [markingID, setMarkingID] = useState(null);
  const { mutateAsync: createDriverSettlement, isPending: creatingDriverSettlement } = useMutateCreateDriverSettlement();
  const markAsDoneCallback = useCallback(async (trip_manifest_id) => {
    setMarkingID(trip_manifest_id);
    try {
      await createDriverSettlement(trip_manifest_id);
    } catch (e) {
      addNotification('error', 'Không tạo được phiếu!');
    }
    setMarkingID(null);
  }, []);

  return (
    <>
      <Box background="bg-fill">
        <div className="driver-trip-manifest">
          <div className="driver-trip-manifest__header">
            {/* <InlineStack align="space-between" blockAlign="center" gap="400"> */}
            <div>
              <Text as="h3" variant="headingLg">
                Tài xế và chuyến
              </Text>
              <Text as="p" tone="subdued">
                Dữ liệu chuyến theo từng tài xế
              </Text>
            </div>
            <DateTimeInput
              initialDate={currentDate}
              onDateChange={(object) => setCurrentDate(`${object.year}-${object.month}-${object.day}`)}
              label={''}
            />
            {/* </InlineStack> */}
          </div>

          <br />

          {loadingDriverVisualization ? (
            <div className="driver-trip-manifest__table">
              <div className="driver-trip-manifest__row driver-trip-manifest__row--heading">
                <div>Tài xế</div>
                <div>Giờ</div>
                <div>Tuyến</div>
                <div>Xe</div>
                <div>Trạng thái</div>
                <div>Khách / hàng</div>
                <div>Tổng thu</div>
                <div>Phí chuyến</div>
                <div>Nộp lại</div>
              </div>
              <BlockStack gap="400">
                <SkeletonBodyText />
                <SkeletonBodyText />
              </BlockStack>
            </div>
          ) : (
            <div className="driver-trip-manifest__table">
              <div className="driver-trip-manifest__row driver-trip-manifest__row--heading">
                <div>Tài xế</div>
                <div>Giờ</div>
                <div>Tuyến</div>
                <div>Xe</div>
                <div>Trạng thái</div>
                <div>Khách / hàng</div>
                <div>Tổng thu</div>
                <div>Phí chuyến</div>
                <div>Nộp lại</div>
              </div>

              {driverVisualizationData?.data?.length === 0 ? (
                <div className="driver-trip-manifest__empty">
                  <Text as="p" alignment="center" tone="subdued">
                    Chưa có chuyến nào cho ngày đã chọn!
                  </Text>
                </div>
              ) : (
                driverVisualizationData?.data?.map((group) =>
                  group.trip_manifests.map((manifest, manifestIndex) => {
                    const isExpanded = expandedManifestId === manifest.id;
                    const netAmount = (manifest.total_amount_due || 0) - (manifest.total_fee || 0);

                    // phục vụ tạo link
                    let trip_date_reformat = dayjs(Number(manifest.departure_date)).format('YYYY-MM-DD');

                    return (
                      <div className="driver-trip-manifest__group" key={`${group.driver.user_id}-${manifest.id}`}>
                        <button
                          className={`driver-trip-manifest__row driver-trip-manifest__row--body ${isExpanded ? 'is-expanded' : ''}`}
                          type="button"
                          onClick={() => setExpandedManifestId(isExpanded ? null : manifest.id)}
                        >
                          <div className="driver-trip-manifest__driver">
                            {manifestIndex === 0 ? (
                              <>
                                <strong>{group.driver.display_name || group.driver.user_phonenumber || 'Chưa có tên tài xế'}</strong>
                                <span>{group.trip_manifests.length} chuyến</span>
                              </>
                            ) : (
                              <span aria-hidden="true" />
                            )}
                          </div>
                          <div>
                            <InlineStack align="start" blockAlign="center" wrap={false} gap={'200'}>
                              {manifest?.driver_settlement ? (
                                <div>
                                  <FontAwesomeIcon color="rgb(3, 123, 93)" icon={faTicket} />
                                </div>
                              ) : null}
                              <div>
                                <Text as="h2" variant="headingMd">
                                  {manifest.departure_hour}
                                </Text>
                                <Text as="p" tone="subdued">
                                  {dayjs(Number(manifest.departure_date)).format('DD-MM-YYYY')}
                                </Text>
                              </div>
                            </InlineStack>
                          </div>
                          <div>
                            <Link
                              url={`/ticket_managers?d=${trip_date_reformat}&journey_id=${manifest.journey_id}&h=${manifest.time_id}`}
                              removeUnderline
                            >
                              <strong>{manifest.journey_name}</strong>
                            </Link>
                          </div>
                          <div>
                            <strong>{manifest.vehicle || '--- ---.--'}</strong>
                          </div>
                          <div>
                            <Badge tone={getTripStatusTone(manifest.trip_status)}>
                              {tripStatusLabels[manifest.trip_status as ENUM_TRIP_MANIFEST_STATUS] || 'Không xác định'}
                            </Badge>
                          </div>
                          <div>{`${manifest.total_occupied_seats || 0} khách ${manifest.total_occupied_good || 0} hàng`}</div>
                          <div>{__helpers.formatNumberWithCommas(manifest.total_fare)}</div>
                          <div>{__helpers.formatNumberWithCommas(manifest.total_fee)}</div>
                          <div className="driver-trip-manifest__return">
                            <span>{__helpers.formatNumberWithCommas(netAmount)}</span>
                            <Icon source={isExpanded ? ChevronDownIcon : ChevronRightIcon} />
                          </div>
                        </button>

                        {isExpanded && (
                          <BlockStack gap="200">
                            <div className="driver-trip-manifest__detail">
                              <div>
                                <span>Biển số</span>
                                <strong>{manifest.vehicle || 'Chưa có biển số'}</strong>
                              </div>
                              <div>
                                <span>Tài phụ</span>
                                <strong>
                                  {group.assistant.length
                                    ? group.assistant.map((assistant) => assistant.display_name || assistant.user_phonenumber).join(', ')
                                    : 'Không có'}
                                </strong>
                              </div>
                              <div>
                                <span>Tổng vé</span>
                                <strong>{manifest.order_data?.length || 0}</strong>
                              </div>
                              <div>
                                <span>Doanh thu vé</span>
                                <strong>{__helpers.formatNumberWithCommas(manifest.total_fare)}</strong>
                              </div>
                              <div>
                                <span>Ghế đã dùng</span>
                                <strong>{manifest.total_occupied_seats || 0}</strong>
                              </div>
                              <div>
                                <span>Số đơn</span>
                                <strong>{manifest.order_data?.length || 0}</strong>
                              </div>
                            </div>
                            <Box padding={'400'}>
                              <Grid columns={{ sm: 3 }}>
                                <Grid.Cell columnSpan={{ xs: 6, sm: 4, md: 4, lg: 8, xl: 8 }}>
                                  <Text as="h3" variant="headingMd">
                                    Danh sách đơn của chuyến
                                  </Text>
                                  <br />
                                  <DataTable
                                    verticalAlign="middle"
                                    columnContentTypes={['text', 'numeric', 'numeric', 'text', 'text', 'text']}
                                    headings={['Mã đơn', 'H.Đơn', 'S.vé', 'Tiền vé', 'Đón-Trả', 'Khách']}
                                    hideScrollIndicator
                                    hasZebraStripingOnData
                                    increasedTableDensity
                                    rows={
                                      manifest?.order_data?.map((order, orderindex) => {
                                        return [
                                          <InlineStack wrap={false}>
                                            <span>
                                              {order.payment_status === ENUM_ORDER_PAYMENT_STATUS.PAID ? (
                                                <FontAwesomeIcon
                                                  icon={
                                                    order.payment_method === ENUM_TRANSACTION_PAYMENT_METHOD.BANK
                                                      ? faQrcode
                                                      : order.payment_method === ENUM_TRANSACTION_PAYMENT_METHOD.CASH
                                                        ? faSackDollar
                                                        : faMinusSquare
                                                  }
                                                />
                                              ) : (
                                                ''
                                              )}
                                            </span>
                                            <span>{`${order.order_code}`}</span>
                                          </InlineStack>,
                                          order.invoices?.shDon || '',
                                          order.total_seat,
                                          __helpers.formatNumberWithCommas(order.total_must_pay),
                                          <BlockStack>
                                            <Text as="p" tone="caution">
                                              {order.pickup_address}
                                            </Text>
                                            <Text as="p" tone="subdued">
                                              {order.dropoff_address}
                                            </Text>
                                          </BlockStack>,
                                          order.customer_data?.display_name + '/ ' + order.customer_data?.user_phonenumber,
                                        ];
                                      }) || []
                                    }
                                  />
                                </Grid.Cell>
                                <Grid.Cell columnSpan={{ xs: 6, sm: 2, md: 2, lg: 4, xl: 4 }}>
                                  <Text as="h3" variant="headingMd">
                                    Danh sách phí của chuyến
                                  </Text>
                                  <br />
                                  {manifest.trip_manifest_fee?.map((f) => {
                                    return (
                                      <InlineStack gap="400" align="start" blockAlign="center">
                                        <Text as="span">{__helpers.formatNumberWithCommas(f.trip_fee)}</Text>
                                        <Text as="span">{f.trip_fee_description}</Text>
                                      </InlineStack>
                                    );
                                  })}
                                  {manifest.trip_manifest_fee.length < 1 ? (
                                    <Text as="p" tone="subdued">
                                      Không có phí nào!
                                    </Text>
                                  ) : null}

                                  <br />

                                  <Capacity current_user_can={'manage_trip_manifest'}>
                                    {manifest.trip_status === ENUM_TRIP_MANIFEST_STATUS.FINALIZED ? (
                                      manifest.driver_settlement ? (
                                        <Button disabled={true} variant="monochromePlain" icon={StatusActiveIcon}>
                                          Đã tạo phiếu quyết toán, tạo lúc{' '}
                                          {dayjs(Number(manifest?.driver_settlement?.createdAt)).tz('Asia/Ho_Chi_Minh').format('HH:mm DD-MM-YYYY')}
                                        </Button>
                                      ) : (
                                        <Button
                                          variant="monochromePlain"
                                          icon={StatusActiveIcon}
                                          loading={creatingDriverSettlement && markingID === manifest.id}
                                          onClick={() => markAsDoneCallback(manifest.id)}
                                        >
                                          Tạo phiếu quyết toán
                                        </Button>
                                      )
                                    ) : null}
                                  </Capacity>
                                </Grid.Cell>
                              </Grid>
                            </Box>
                            <Divider />
                          </BlockStack>
                        )}
                      </div>
                    );
                  })
                )
              )}

              <div className="driver-trip-manifest__row driver-trip-manifest__row--heading">
                <div>Tổng: {__helpers.formatNumberWithCommas(totalInTable.total_manifest)} chuyến</div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div>{__helpers.formatNumberWithCommas(totalInTable.total_fare)}</div>
                <div>{__helpers.formatNumberWithCommas(totalInTable.total_fee)}</div>
                <div>{__helpers.formatNumberWithCommas(totalInTable.total_due)}</div>
              </div>
            </div>
          )}
        </div>
      </Box>
      <Capacity current_user_can={'view_finance_widget'}>
        <Box background="bg-fill" padding={'400'}>
          <Text as="p">Trong bảng này</Text>
          <br />
          <InlineStack align="start" blockAlign="center" gap="600">
            <div>
              <Text as="p" tone="subdued">
                Tổng giao dịch
              </Text>
              <Text as="p" variant="headingLg">
                {__helpers.formatNumberWithCommas(driverVisualizationData?.transaction_data?.total_payment_transaction || 0)}
              </Text>
            </div>
            <div>
              <Text as="p" tone="subdued">
                Tổng tiền vé
              </Text>
              <Text as="p" variant="headingLg">
                {__helpers.formatNumberWithCommas(driverVisualizationData?.transaction_data?.total_expected_paid || 0)}
              </Text>
            </div>
            <div>
              <Text as="p" tone="subdued">
                Chưa thu
              </Text>
              <Text as="p" variant="headingLg">
                {__helpers.formatNumberWithCommas(driverVisualizationData?.transaction_data?.total_unpaid)}
              </Text>
            </div>
            <div>
              <Text as="p" tone="subdued">
                Công nợ
              </Text>
              <Text as="p" variant="headingLg">
                {__helpers.formatNumberWithCommas(driverVisualizationData?.transaction_data?.total_debit)}
              </Text>
            </div>
            <div>
              <Text as="p" tone="subdued">
                Chuyển khoản
              </Text>
              <Text as="p" variant="headingLg">
                {__helpers.formatNumberWithCommas(driverVisualizationData?.transaction_data?.total_payment_bank || 0)}
              </Text>
            </div>
            <div>
              <Text as="p" tone="subdued">
                Tiền mặt
              </Text>
              <Text as="p" variant="headingLg">
                {__helpers.formatNumberWithCommas(driverVisualizationData?.transaction_data?.total_payment_cash || 0)}
              </Text>
            </div>
            <div>
              <Text as="p" tone="subdued">
                Tổng phí
              </Text>
              <Text as="p" variant="headingLg">
                {__helpers.formatNumberWithCommas(driverVisualizationData?.transaction_data?.total_trip_manifest_fee || 0)}
              </Text>
            </div>
          </InlineStack>
        </Box>
      </Capacity>
    </>
  );
}

